/**
 * EditScenariosInputSelectionCard body (Gate 1).
 *
 * Spec: v2-design/CARD_SHELL_v0_spec.md §5 (per-control inline edit).
 *
 * Layout:
 *   Input set:    <picker over selected + candidates>
 *                    (coupled-member coupling_rationale shown as italic note)
 *   Output:       <picker over candidates>   (segments indented beneath)
 *   Timescale:    <Monthly / Quarterly / Yearly toggle>
 *   Chart name:   <text input>
 *   Scenario:     <name (read-only)>   (immutable — frozen by Enumerator)
 *
 * Edit affordances (parallels CV/DA):
 *   - Input set: MUI Select over input_set.selected + input_set.candidates
 *     (same pattern as OutputPicker). Renders as read-only display when
 *     candidates is empty. Coupling rationale on coupled members rendered
 *     inline as italic secondary text.
 *   - Output: same OutputPicker pattern as CV/DA — Select over candidates,
 *     dispatches edit_field({ output }).
 *   - Timescale: toggle group, dispatches edit_field({ timescale }).
 *   - Chart name: TextField, blur-commit (matches CV).
 *   - Scenario: NO edit affordance. Schema marks immutable. Scenario
 *     revisions route through the Enumerator as a new plan with
 *     parent_plan_id.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { OutputAndSegmentsBlock } from '../shared/OutputAndSegmentsBlock';
import { SelectionRationalePopover } from '../shared/SelectionRationalePopover';

import {
  isTimescaleSelectable,
  parseSelectableTimescales,
  TIMESCALE_OPTIONS,
  TimescaleLiteral,
  UNAVAILABLE_TIMESCALE_TOOLTIP,
} from './timescaleOptions';

interface DimensionInstance {
  name: string;
  id: number;
}

interface DimensionSegment {
  dimension_depth: number;
  dimension_name: string;
  instances: DimensionInstance[];
}

interface SelectedOutput {
  name: string;
  id: number;
  selection_rationale?: string | null;
}

interface OutputCandidate {
  name: string;
  id: number;
  similarity?: number | null;
}

interface OutputSelection {
  selected: SelectedOutput;
  candidates: OutputCandidate[];
}

interface InputSetEntry {
  name: string;
  id: number;
  coupling_rationale: string | null;
}

interface SelectedInputSet {
  inputs: InputSetEntry[];
  selection_rationale?: string | null;
}

interface InputSetCandidate {
  inputs: InputSetEntry[];
  similarity?: number | null;
}

interface InputSetSelection {
  selected: SelectedInputSet;
  candidates: InputSetCandidate[];
}

interface EditScenariosInputSelectionBodyProps {
  content: Record<string, unknown>;
  onEditField: (field_updates: Record<string, unknown>) => void;
  editsDisabled: boolean;
}

export const EditScenariosInputSelectionBody: React.FC<
  EditScenariosInputSelectionBodyProps
> = ({ content, onEditField, editsDisabled }) => {
  const inputSet = content.input_set as InputSetSelection | undefined;
  const output = content.output as OutputSelection;
  const segments = (content.segments ?? []) as DimensionSegment[];
  const segmentRationale =
    typeof content.segment_rationale === 'string' && content.segment_rationale
      ? content.segment_rationale
      : undefined;

  const timescale = content.timescale ? String(content.timescale) : null;
  const timescaleNote =
    typeof content.timescale_note === 'string' && content.timescale_note
      ? content.timescale_note
      : undefined;
  const selectableTimescales = parseSelectableTimescales(
    content.selectable_timescales
  );

  const chartName =
    typeof content.chart_name === 'string' ? content.chart_name : '';

  const scenarioName =
    typeof content.scenario_name === 'string' ? content.scenario_name : '';

  return (
    <Stack spacing={1.5}>
      {inputSet ? (
        <InputSetControl
          inputSet={inputSet}
          onEditField={onEditField}
          disabled={editsDisabled}
        />
      ) : null}

      <OutputAndSegmentsBlock
        output={output}
        segments={segments}
        segmentRationale={segmentRationale}
        outputControl={
          <OutputPicker
            output={output}
            onEditField={onEditField}
            disabled={editsDisabled}
          />
        }
      />

      <TimescaleControl
        timescale={timescale}
        timescaleNote={timescaleNote}
        selectableTimescales={selectableTimescales}
        onEditField={onEditField}
        disabled={editsDisabled}
      />

      <ChartNameControl
        chartName={chartName}
        onEditField={onEditField}
        disabled={editsDisabled}
      />

      <ScenarioContext scenarioName={scenarioName} />
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Input set control — Select over selected + candidates (mirrors OutputPicker).
// Renders as read-only display when candidates is empty. Coupled-member
// coupling_rationale rendered inline as italic secondary text.
// ---------------------------------------------------------------------------

interface InputSetControlProps {
  inputSet: InputSetSelection;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

type InputSetPick = SelectedInputSet | InputSetCandidate;

const InputSetControl: React.FC<InputSetControlProps> = ({
  inputSet,
  onEditField,
  disabled,
}) => {
  const rationale = inputSet.selected.selection_rationale ?? null;
  return (
    <FieldRow label="Input">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InputSetPicker
          inputSet={inputSet}
          onEditField={onEditField}
          disabled={disabled}
        />
        {rationale ? (
          <SelectionRationalePopover
            rationale={rationale}
            ariaLabel="Why this Input was selected"
          />
        ) : null}
      </Box>
    </FieldRow>
  );
};

interface InputSetPickerProps {
  inputSet: InputSetSelection;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

// Build an option list of input-set picks. Each option's value is a
// stable comma-joined ID string of the set's member IDs (so a coupled
// cluster picks as a unit). The Select returns that string; we map back
// to the full set on commit. When candidates is empty, the Select has
// only one option and acts as a read-only display.
const InputSetPicker: React.FC<InputSetPickerProps> = ({
  inputSet,
  onEditField,
  disabled,
}) => {
  const options = useMemo<
    { key: string; label: string; set: InputSetPick }[]
  >(() => {
    const all: InputSetPick[] = [inputSet.selected, ...inputSet.candidates];
    return all.map((set) => ({
      key: set.inputs.map((i) => i.id).join(','),
      label: setLabel(set),
      set,
    }));
  }, [inputSet.selected, inputSet.candidates]);

  const selectedKey = useMemo(
    () => inputSet.selected.inputs.map((i) => i.id).join(','),
    [inputSet.selected]
  );

  const effectivelyDisabled = disabled || inputSet.candidates.length === 0;

  const handleChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const nextKey = String(e.target.value);
      if (nextKey === selectedKey) return;
      const pick = options.find((o) => o.key === nextKey);
      if (!pick) return;
      // Swap pick into selected; move previous selected into candidates.
      // Drop selection_rationale on swap — it described the previous pick.
      const previouslySelected: InputSetCandidate = {
        inputs: inputSet.selected.inputs,
      };
      const nextCandidates: InputSetCandidate[] = inputSet.candidates
        .filter((c) => c.inputs.map((i) => i.id).join(',') !== pick.key)
        .concat([previouslySelected]);
      onEditField({
        input_set: {
          selected: {
            inputs: pick.set.inputs,
            selection_rationale: null,
          },
          candidates: nextCandidates,
        },
      });
    },
    [onEditField, options, selectedKey, inputSet]
  );

  return (
    <Select
      value={selectedKey}
      onChange={handleChange}
      size="small"
      disabled={effectivelyDisabled}
      variant="outlined"
      sx={{
        minWidth: 240,
        maxWidth: 360,
        fontSize: 13,
        '& .MuiSelect-select': { py: 0.5 },
      }}
      MenuProps={{
        PaperProps: { sx: { maxHeight: 320 } },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.key} value={opt.key} sx={{ fontSize: 13 }}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  );
};

function setLabel(set: InputSetPick): string {
  // For singletons: the input name. For multi-Input clusters: comma-join
  // with the user-named member first. Matches OutputPicker's bare-access
  // pattern — if name is empty, this renders empty (loud fail). A
  // Pydantic min_length validator on InputSetEntry.name catches the
  // empty-name case at the backend boundary before it reaches here.
  if (set.inputs.length === 0) return '(empty)';
  if (set.inputs.length === 1) return set.inputs[0].name;
  const userNamed = set.inputs.filter((i) => i.coupling_rationale === null);
  const coupled = set.inputs.filter((i) => i.coupling_rationale !== null);
  return [...userNamed, ...coupled].map((i) => i.name).join(' + ');
}

// ---------------------------------------------------------------------------
// Output picker — same pattern as DriverAnalysisParamBody / CV body.
// ---------------------------------------------------------------------------

interface OutputPickerProps {
  output: OutputSelection;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const OutputPicker: React.FC<OutputPickerProps> = ({
  output,
  onEditField,
  disabled,
}) => {
  const options = useMemo<OutputCandidate[]>(() => {
    const selectedOption: OutputCandidate = {
      name: output.selected.name,
      id: output.selected.id,
    };
    return [selectedOption, ...output.candidates];
  }, [output.selected, output.candidates]);

  const handleChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      const nextId = Number(e.target.value);
      if (!Number.isFinite(nextId) || nextId === output.selected.id) return;
      const pick = options.find((o) => o.id === nextId);
      if (!pick) return;
      const nextCandidates: OutputCandidate[] = output.candidates
        .filter((c) => c.id !== pick.id)
        .concat(
          pick.id === output.selected.id
            ? []
            : [{ name: output.selected.name, id: output.selected.id }]
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      onEditField({
        output: {
          selected: { id: pick.id, name: pick.name },
          candidates: nextCandidates,
        },
      });
    },
    [onEditField, options, output.candidates, output.selected]
  );

  return (
    <Select
      value={output.selected.id}
      onChange={handleChange}
      size="small"
      disabled={disabled}
      variant="outlined"
      sx={{
        minWidth: 240,
        maxWidth: 360,
        fontSize: 13,
        '& .MuiSelect-select': { py: 0.5 },
      }}
      MenuProps={{
        PaperProps: { sx: { maxHeight: 320 } },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: 13 }}>
          {opt.name}
        </MenuItem>
      ))}
    </Select>
  );
};

// ---------------------------------------------------------------------------
// Timescale control — toggle group (CV pattern; ES also has no dates).
// ---------------------------------------------------------------------------

interface TimescaleControlProps {
  timescale: string | null;
  timescaleNote?: string;
  selectableTimescales: TimescaleLiteral[] | null;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const TimescaleControl: React.FC<TimescaleControlProps> = ({
  timescale,
  timescaleNote,
  selectableTimescales,
  onEditField,
  disabled,
}) => {
  const handleChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, next: TimescaleLiteral | null) => {
      if (!next || next === timescale) return;
      onEditField({ timescale: next });
    },
    [onEditField, timescale]
  );

  return (
    <FieldRow label="Timescale">
      <Stack spacing={1}>
        <ToggleButtonGroup
          value={timescale ?? ''}
          exclusive
          onChange={handleChange}
          size="small"
          disabled={disabled}
          aria-label="Timescale"
        >
          {TIMESCALE_OPTIONS.map((opt) => {
            // Disabled rather than hidden: a toggle silently losing a
            // button reads as a bug, and the tooltip says why.
            //
            // ToggleButton must stay the DIRECT child of
            // ToggleButtonGroup — the group clones its direct children to
            // inject selected/onChange and the grouped borders, so
            // wrapping the button itself would break selection and
            // clicking. The tooltip therefore anchors to the label
            // inside, with pointerEvents restored because a disabled MUI
            // button sets pointer-events: none on its root.
            const selectable = isTimescaleSelectable(
              opt.value,
              selectableTimescales
            );
            return (
              <ToggleButton
                key={opt.value}
                value={opt.value}
                disabled={disabled || !selectable}
                sx={{ fontSize: 12, textTransform: 'none', px: 1.5, py: 0.25 }}
              >
                {selectable ? (
                  opt.label
                ) : (
                  <Tooltip title={UNAVAILABLE_TIMESCALE_TOOLTIP}>
                    <span style={{ pointerEvents: 'auto' }}>{opt.label}</span>
                  </Tooltip>
                )}
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>

        {timescaleNote ? (
          <Typography
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              fontStyle: 'italic',
            }}
          >
            {timescaleNote}
          </Typography>
        ) : null}
      </Stack>
    </FieldRow>
  );
};

// ---------------------------------------------------------------------------
// Chart name control — TextField, blur-commits (CV pattern).
// ---------------------------------------------------------------------------

interface ChartNameControlProps {
  chartName: string;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const ChartNameControl: React.FC<ChartNameControlProps> = ({
  chartName,
  onEditField,
  disabled,
}) => {
  const [draft, setDraft] = useState(chartName);

  useEffect(() => {
    setDraft(chartName);
  }, [chartName]);

  const handleBlur = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed === (chartName ?? '').trim()) return;
    onEditField({ chart_name: trimmed });
  }, [draft, chartName, onEditField]);

  return (
    <FieldRow label="Chart name">
      <TextField
        size="small"
        variant="outlined"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder="Untitled chart"
        inputProps={{ 'aria-label': 'Chart name' }}
        sx={{
          minWidth: 240,
          maxWidth: 480,
          '& .MuiInputBase-input': { fontSize: 13 },
          '& input': { fontSize: 13 },
        }}
      />
    </FieldRow>
  );
};

// ---------------------------------------------------------------------------
// Scenario context — read-only display. scenario_id/scenario_name are
// immutable per the card schema; scenario revisions route through the
// Enumerator as a new plan. No edit affordance on this card.
// ---------------------------------------------------------------------------

interface ScenarioContextProps {
  scenarioName: string;
}

const ScenarioContext: React.FC<ScenarioContextProps> = ({ scenarioName }) => {
  return (
    <FieldRow label="Scenario">
      <Typography
        sx={{
          fontSize: 13,
          lineHeight: 1.5,
          wordBreak: 'break-word',
        }}
      >
        {scenarioName || '—'}
      </Typography>
    </FieldRow>
  );
};

// ---------------------------------------------------------------------------
// FieldRow — local helper matching the label / control row layout used by
// CV's TimescaleControl + ChartNameControl. Kept local to this file to
// avoid an unscoped shared change.
// ---------------------------------------------------------------------------

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: 'text.secondary',
          width: 120,
          flexShrink: 0,
          lineHeight: 1.5,
          pt: 0.75,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Box>
  );
};
